import { useLang } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  BatteryCharging,
  GridFour,
  Broadcast,
  Brain,
  Handshake,
  ArrowRight,
  CheckCircle,
  SealCheck,
  ArrowSquareOut,
} from "@phosphor-icons/react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const icons = [BatteryCharging, GridFour, Broadcast, Brain, Handshake];

// ── Partner constants (easy to update) ──
const TIANNENG_WEBSITE_URL = "https://www.tiannengglobal.com/";
const TIANNENG_LOGO_SRC = "https://customer-assets.emergentagent.com/job_gjc-latam-hub/artifacts/s7i8gahe_logo-h.png";

export default function Solutions() {
  const { t } = useLang();

  return (
    <div data-testid="solutions-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4"
          >
            {t.solutions.overline}
          </motion.p>
          <motion.h1
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#0A192F] max-w-4xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.solutions.headline}
          </motion.h1>
        </div>
      </section>

      {/* Solution Categories */}
      {t.solutions.categories.map((cat, i) => {
        const Icon = icons[i];
        const isAlt = i % 2 === 1;
        return (
          <section
            key={i}
            className={`py-20 md:py-28 ${isAlt ? "bg-[#F8FAFC]" : "bg-white"}`}
            data-testid={`solution-category-${i}`}
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                >
                  <Icon size={44} weight="duotone" className="text-[#0D9488] mb-6" />
                  <h2
                    className="text-3xl md:text-4xl font-medium tracking-tight text-[#0A192F] mb-4"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {cat.title}
                  </h2>
                  <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                    {cat.desc}
                  </p>
                </motion.div>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={1}
                  variants={fadeUp}
                >
                  <ul className="space-y-4">
                    {cat.items.map((item, j) => (
                      <li key={j} className="flex gap-3 items-start">
                        <CheckCircle size={22} weight="duotone" className="text-[#0D9488] shrink-0 mt-0.5" />
                        <span className="text-base text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* ===== PARTNERS ===== */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="solutions-partners-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4"
          >
            {t.partners.overline}
          </motion.p>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-medium tracking-tight text-[#0A192F] mb-16 max-w-2xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.partners.title}
          </motion.h2>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
            variants={fadeUp}
            className="border border-slate-200 bg-white p-8 md:p-10 max-w-3xl"
            data-testid="solutions-partner-tianneng"
          >
            <div className="flex flex-col sm:flex-row items-start gap-6 mb-6">
              <a href={TIANNENG_WEBSITE_URL} target="_blank" rel="noopener noreferrer" className="shrink-0">
                <img
                  src={TIANNENG_LOGO_SRC}
                  alt="Tianneng"
                  className="h-14 w-auto object-contain"
                />
              </a>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <SealCheck size={20} weight="fill" className="text-[#0D9488]" />
                  <span className="text-xs uppercase tracking-[0.15em] font-bold text-[#0D9488]">
                    {t.partners.tianneng.badge}
                  </span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t.partners.tianneng.description}
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-700 font-medium mb-2">
              {t.partners.tianneng.microcopy}
            </p>
            <a
              href={TIANNENG_WEBSITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="solutions-tianneng-link"
              className="inline-flex items-center text-sm font-medium text-[#0D9488] hover:text-[#0F766E] transition-colors mb-6"
            >
              {t.partners.tianneng.visitWebsite} <ArrowSquareOut size={16} className="ml-1.5" />
            </a>
            <div className="border-t border-slate-200 pt-5 mt-2">
              <p className="text-xs text-slate-500 leading-relaxed">
                {t.partners.tianneng.disclaimer}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A192F]" data-testid="solutions-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-medium tracking-tight text-white mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.contact.headline}
          </motion.h2>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
          >
            <Link
              to="/contact"
              data-testid="solutions-contact-cta"
              className="inline-flex items-center bg-[#0D9488] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#0F766E] transition-colors duration-300"
            >
              {t.nav.talkToUs} <ArrowRight size={18} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
